/**
 * feat-338 — the page's filters live in the URL query string, so a filtered view is a
 * shareable link and Back works. `filters` is exactly what the API is sent;
 * `flaggedOnly` is applied client-side (the contract has no flag filter).
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { LearnLang, QuestionAnalysisFilters, QuestionType } from '../../../../services/questionAnalysisService';

export interface QaFilterState extends QuestionAnalysisFilters {
  flagged_only?: boolean;
}

const LANGS: LearnLang[] = ['en', 'es'];
const TYPES: QuestionType[] = ['multiple_choice', 'fill_blank'];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function parseFilters(sp: URLSearchParams): QaFilterState {
  const s: QaFilterState = {};
  const start = sp.get('start');
  const end = sp.get('end');
  if (start && DATE_RE.test(start)) s.start = start;
  if (end && DATE_RE.test(end)) s.end = end;
  const loc = sp.get('location_id');
  if (loc) s.location_id = loc;
  const lang = sp.get('lang') as LearnLang | null;
  if (lang && LANGS.includes(lang)) s.lang = lang;
  const qt = sp.get('question_type') as QuestionType | null;
  if (qt && TYPES.includes(qt)) s.question_type = qt;
  const drill = sp.get('drill_id');
  if (drill) s.drill_id = drill;
  if (sp.get('include_earlier_versions') === 'true') s.include_earlier_versions = true;
  if (sp.get('flagged_only') === 'true') s.flagged_only = true;
  return s;
}

export function serializeFilters(s: QaFilterState): URLSearchParams {
  const p = new URLSearchParams();
  (['start', 'end', 'location_id', 'lang', 'question_type', 'drill_id'] as const).forEach((k) => {
    const v = s[k];
    if (v) p.set(k, v);
  });
  if (s.include_earlier_versions) p.set('include_earlier_versions', 'true');
  if (s.flagged_only) p.set('flagged_only', 'true');
  return p;
}

/** The API filters only (drops `flagged_only`). */
export function apiFilters(s: QaFilterState): QuestionAnalysisFilters {
  const { flagged_only: _ignored, ...rest } = s;
  return rest;
}

/** True when anything narrower than "all questions" is selected (dates excluded). */
export function hasNarrowingFilter(s: QaFilterState): boolean {
  return !!(s.location_id || s.lang || s.question_type || s.drill_id || s.flagged_only);
}

export function useQaFilters() {
  const [sp, setSp] = useSearchParams();
  const key = sp.toString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filters = useMemo(() => parseFilters(sp), [key]);
  const update = useCallback(
    (patch: Partial<QaFilterState>) => {
      setSp((cur) => serializeFilters({ ...parseFilters(cur), ...patch }), { replace: true });
    },
    [setSp],
  );
  const reset = useCallback(() => setSp(new URLSearchParams(), { replace: true }), [setSp]);
  return { filters, update, reset };
}
